import { EntityListPage, type FilterField, type DataTableColumn, type CardConfig } from '@/components/common'
import { SearchableSelect } from '@/components/common/SearchableSelect'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { useTranslation } from '@/contexts/TranslationContext'
import { useUnit } from '@/contexts/UnitContext'
import { matchesSearch } from '@/lib/search'
import { useEffect, useState } from 'react'
import {
  listPricingInputs,
  createPricingInput,
  updatePricingInput,
  deletePricingInput,
  type PricingInput
} from '@/lib/services/pricingInputs'
import { listMeasures, type Measure } from '@/lib/services/measures'

interface PricingInputFormData {
  name: string
  uom: string
  price: string
}

export function PricingMaterials() {
  const { t } = useTranslation()
  const { currentUnit } = useUnit()
  const [measures, setMeasures] = useState<Measure[]>([])
  const [measuresLoading, setMeasuresLoading] = useState(true)

  const unitId = currentUnit?.id
  if (!unitId) {
    return <div className="p-6 text-red-600">Unidade não selecionada</div>
  }

  // Load measures on component mount
  useEffect(() => {
    const loadMeasures = async () => {
      try {
        setMeasuresLoading(true)
        const data = await listMeasures()
        setMeasures(data)
      } catch (error) {
        console.error('Error loading measures:', error)
        setMeasures([])
      } finally {
        setMeasuresLoading(false)
      }
    }
    loadMeasures()
  }, [])

  // Filter fields configuration
  const filterFields: FilterField[] = [
    {
      key: 'name',
      label: 'Descrição',
      type: 'text',
      placeholder: 'Filtrar por descrição...'
    },
    {
      key: 'uom',
      label: 'Unidade',
      type: 'text',
      placeholder: 'Filtrar por unidade...'
    }
  ]

  // Table columns configuration
  const tableColumns: DataTableColumn<PricingInput>[] = [
    {
      key: 'name',
      label: 'Descrição',
      render: (input) => (
        <div className="font-medium text-slate-900 dark:text-white">{input.name}</div>
      )
    },
    {
      key: 'uom',
      label: 'Unidade',
      render: (input) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200">
          {input.uom}
        </span>
      )
    },
    {
      key: 'price',
      label: 'Preço',
      render: (input) => (
        <span className="text-slate-700 dark:text-slate-300">
          {input.price ? `R$ ${input.price.toFixed(2)}` : '-'}
        </span>
      )
    }
  ]

  // Entity configuration
  const config = {
    entityType: 'pricing-inputs',
    filterFields,

    loadData: async () => {
      return await listPricingInputs(unitId)
    },

    createEntity: async (data: PricingInputFormData) => {
      const price = data.price ? parseFloat(data.price) : undefined
      return await createPricingInput(unitId, {
        name: data.name.trim(),
        uom: data.uom.trim(),
        price
      })
    },

    updateEntity: async (id: string, data: PricingInputFormData) => {
      const price = data.price ? parseFloat(data.price) : undefined
      return await updatePricingInput(id, {
        name: data.name.trim(),
        uom: data.uom.trim(),
        price
      })
    },

    deleteEntity: async (id: string) => {
      await deletePricingInput(id)
    },

    applyFilters: (inputs: PricingInput[], searchQuery: string, filterValues: any) => {
      return inputs.filter((input) => {
        // Quick search
        if (searchQuery.trim()) {
          if (!matchesSearch(input.name, searchQuery) && !matchesSearch(input.uom, searchQuery)) {
            return false
          }
        }

        // Advanced filters
        if (filterValues.name && filterValues.name.trim()) {
          if (!matchesSearch(input.name, filterValues.name)) {
            return false
          }
        }

        if (filterValues.uom && filterValues.uom.trim()) {
          if (!matchesSearch(input.uom, filterValues.uom)) {
            return false
          }
        }

        return true
      })
    },

    validateForm: (data: PricingInputFormData) => {
      const errors: Record<string, string> = {}

      if (!data.name.trim()) {
        errors.name = 'Descrição é obrigatória'
      } else if (data.name.trim().length < 2) {
        errors.name = 'Descrição deve ter pelo menos 2 caracteres'
      }

      if (!data.uom.trim()) {
        errors.uom = 'Unidade é obrigatória'
      } else if (data.uom.trim().length < 1) {
        errors.uom = 'Unidade é obrigatória'
      }

      if (data.price && isNaN(parseFloat(data.price))) {
        errors.price = 'Preço deve ser um número válido'
      }

      return errors
    }
  }

  // Labels
  const labels = {
    title: 'Insumos e Materiais',
    description: 'Gerencie os insumos e materiais utilizados nos seus produtos',
    newButton: 'Novo Insumo',
    searchPlaceholder: 'Buscar insumo...',
    emptyTitle: 'Nenhum insumo cadastrado',
    emptyDescription: 'Comece adicionando seus primeiros insumos e materiais',
    emptyActionLabel: 'Novo Insumo',
    modalTitleNew: 'Novo Insumo',
    modalTitleEdit: 'Editar Insumo',
    modalTitleView: 'Visualizar Insumo',
    recordTitle: 'Insumo',
    deleteTitle: 'Deletar Insumo',
    deleteMessage: 'Tem certeza que deseja deletar este insumo? Esta ação não pode ser desfeita.',
    deleteButton: 'Deletar',
    cancelButton: 'Cancelar',
    submitButton: 'Criar Insumo',
    updateButton: 'Atualizar Insumo',
    viewCard: 'Visualização em Cards',
    viewTable: 'Visualização em Tabela',
    clearSearch: 'Limpar Busca'
  }

  // Icon
  const icon = (
    <svg className="h-7 w-7 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
  )

  // Card configuration
  const cardConfig: CardConfig<PricingInput> = {
    getIcon: () => (
      <svg className="h-5 w-5 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
      </svg>
    ),
    getTitle: (input) => input.name,
    getSubtitle: (input) => `Unidade: ${input.uom}`,
    fields: [
      {
        key: 'uom',
        label: 'Unidade de Medida',
        getValue: (input) => input.uom
      },
      {
        key: 'price',
        label: 'Preço',
        getValue: (input) => (input.price ? `R$ ${input.price.toFixed(2)}` : 'Não definido')
      }
    ],
    editLabel: 'Editar',
    deleteLabel: 'Deletar'
  }

  // Form render
  const renderForm = (
    formData: PricingInputFormData,
    setFormData: (data: PricingInputFormData) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
    editingEntity: PricingInput | null,
    mode: 'create' | 'edit' | 'view'
  ) => {
    // Build searchable options from measures
    const measureOptions = measures.map((measure) => ({
      value: measure.code,
      label: measure.name,
      meta: measure.symbol
    }))

    return (
    <div className="space-y-4">
      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Descrição *
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Ex: Ouro 18k, Óleo vegetal, Madeira pinus..."
          className={`w-full px-4 py-2 rounded-lg border ${
            formErrors.name ? 'border-red-300' : 'border-slate-300'
          } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500`}
          disabled={submitting || mode === 'view'}
          autoFocus
        />
        {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
      </div>

      {/* UOM */}
      <div>
        <label htmlFor="uom" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Unidade de Medida *
        </label>
        {measuresLoading ? (
          <div className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 flex items-center gap-2">
            <div className="animate-spin h-4 w-4 border-2 border-slate-400 border-t-slate-600"></div>
            Carregando unidades...
          </div>
        ) : (
          <SearchableSelect
            options={measureOptions}
            value={formData.uom}
            onChange={(code) => setFormData({ ...formData, uom: code })}
            placeholder="Buscar unidade de medida..."
            disabled={submitting || mode === 'view'}
            error={!!formErrors.uom}
          />
        )}
        {formErrors.uom && <p className="mt-1 text-sm text-red-600">{formErrors.uom}</p>}
      </div>

      {/* Price */}
      <div>
        <label htmlFor="price" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          Preço Unitário (Opcional)
        </label>
        <div className="flex items-center gap-2">
          <span className="text-slate-600 dark:text-slate-400">R$</span>
          <CurrencyInput
            id="price"
            value={formData.price}
            onChange={(value) => setFormData({ ...formData, price: value })}
            placeholder="0,00"
            disabled={submitting || mode === 'view'}
            error={!!formErrors.price}
          />
        </div>
        {formErrors.price && <p className="mt-1 text-sm text-red-600">{formErrors.price}</p>}
      </div>
    </div>
    )
  }

  return (
    <EntityListPage<PricingInput, PricingInputFormData>
      config={config}
      labels={labels}
      icon={icon}
      filterFields={filterFields}
      tableColumns={tableColumns}
      cardConfig={cardConfig}
      renderForm={renderForm}
      entityToFormData={(input) => ({
        name: input.name,
        uom: input.uom,
        price: input.price ? String(input.price) : ''
      })}
      emptyFormData={() => ({
        name: '',
        uom: '',
        price: ''
      })}
    />
  )
}
